import Favorite from '../community/Favorite'
import React, { useEffect, useState } from 'react'
import Submitting from '../common/Submitting'
import currency from 'currency.js'
import pluralize from 'pluralize'
import {
  InputError,
  Action,
  Button,
  Collapsible,
  themeConWeb,
} from '@foodsby/nutrient'
import { Link } from 'react-router-dom'
import { formatUrl } from '../../util/formatUtils'
import { getIsSubscribed } from '../../api/api'
import { placeOrderRoute } from '../../routes/routes'
import { useStateValue } from '../../providers/ProviderCheckout'

export const CartAnswerItem = ({
  answer: {
    menuItem: { displayText, price },
  },
}) => (
  <li>
    <span>{displayText}</span>
    {price ? (
      <small className="cart-item_addon">{`+ ${currency(price, {
        formatWithSymbol: true,
      }).format()}`}</small>
    ) : null}
  </li>
)

export const CartModifierList = ({
  deleteMenuItem,
  dropoffId,
  editMenuItem,
  editable,
  isEditingPastOrder,
  modifiers,
  orderItemId,
  showEditLinks,
  specialInstructions,
}) => (
  <>
    {editable && showEditLinks && (
      <div className="row end-xs">
        <div className="col-xs">
          <Action
            className="cart-item_edit"
            onClick={() => {
              editMenuItem(null, orderItemId, isEditingPastOrder)
            }}
            title="Edit Menu Item"
          >
            <i className="icon icon-pencil" /> Edit
          </Action>
        </div>
        <div className="col-xs">
          <Action
            className="cart-item_delete"
            onClick={() => {
              deleteMenuItem(orderItemId, dropoffId)
            }}
            title="Delete Menu Item"
          >
            <i className="icon icon-trash" /> Remove
          </Action>
        </div>
      </div>
    )}
    <ul className="cart-item_modifier_list">
      {modifiers &&
        modifiers.map(modifier =>
          modifier.answers.map((answer, index) => (
            <CartAnswerItem answer={answer} key={index} />
          )),
        )}
      {specialInstructions && (
        <div className="cart-item_description cart-item_instructions">
          <strong>Special Instructions: </strong>
          <span className="cart-special_instructions">
            {specialInstructions}
          </span>
        </div>
      )}
    </ul>
  </>
)

export const CartNoMenuItems = () => (
  <li className="cart-item">
    <div className="cart-item_summary">
      <div className="cart-item_none">No items</div>
    </div>
  </li>
)

export const CartPreviousItems = ({ numberOfItems, subtotal }) => (
  <li className="cart-item">
    <div className="cart-item_summary">
      <div className="cart-item_title">{`${pluralize(
        'other item',
        numberOfItems,
        true,
      )} (In Cart)`}</div>
      <div className="cart-item_price">
        {currency(subtotal, { formatWithSymbol: true }).format()}
      </div>
    </div>
  </li>
)

export const CartCollapsibleModifierHeader = ({ open }) => (
  <Action className="cart-item_modifier_title">
    {open ? '- Hide' : '+ Show'} Details
  </Action>
)

export const CartCollapsibleModifierList = ({
  deleteMenuItem,
  dropoffId,
  editMenuItem,
  isEditingPastOrder,
  modifiers,
  open,
  orderItemId,
  showEditLinks,
  specialInstructions,
}) => (
  <Collapsible
    open={open}
    trigger={<CartCollapsibleModifierHeader />}
    triggerwhenopen={<CartCollapsibleModifierHeader open />}
  >
    <CartModifierList
      deleteMenuItem={deleteMenuItem}
      dropoffId={dropoffId}
      editable
      editMenuItem={editMenuItem}
      isEditingPastOrder={isEditingPastOrder}
      modifiers={modifiers}
      orderItemId={orderItemId}
      showEditLinks={showEditLinks}
      specialInstructions={specialInstructions}
    />
  </Collapsible>
)

export const CartMenuItem = ({
  menuItem: {
    fullSubTotal,
    menuItem: { displayText, price },
    menuItemId,
    modifierSubTotal,
    modifiers,
    orderItemId,
    specialInstructions,
  },
  collapsible,
  deleteMenuItem,
  dropoffId,
  editMenuItem,
  isEditingPastOrder,
  menuId,
  open,
  showEditLinks,
  useFullSubTotal,
  editable,
}) => {
  const mods =
    (modifiers && modifiers.length > 0 && modifiers[0].answers.length > 0) ||
    specialInstructions ? (
      collapsible ? (
        <CartCollapsibleModifierList
          deleteMenuItem={deleteMenuItem}
          dropoffId={dropoffId}
          editable
          editMenuItem={editMenuItem}
          isEditingPastOrder={isEditingPastOrder}
          modifiers={modifiers}
          modifierSubTotal={modifierSubTotal}
          open={open}
          orderItemId={orderItemId}
          showEditLinks={showEditLinks}
          specialInstructions={specialInstructions}
        />
      ) : (
        <CartModifierList
          dropoffId={dropoffId}
          isEditingPastOrder={isEditingPastOrder}
          modifiers={modifiers}
          showEditLinks={showEditLinks}
          specialInstructions={specialInstructions}
        />
      )
    ) : null
  const total = useFullSubTotal ? fullSubTotal : price
  return (
    <li className="cart-item">
      {!editable && (
        <Favorite
          classNames={{ 'cart-item_favorite': true }}
          id={menuItemId}
          menuId={menuId}
          style={{ marginTop: '0px' }}
        />
      )}

      <div className="cart-item_summary">
        <div className="cart-item_title">{displayText}</div>
        <div className="cart-item_price">
          {currency(total, { formatWithSymbol: true }).format()}
        </div>
      </div>
      {(modifiers &&
        modifiers.length === 0 &&
        showEditLinks &&
        !specialInstructions) ||
      (modifiers &&
        modifiers.length === 1 &&
        modifiers[0] &&
        modifiers[0].answers &&
        modifiers[0].answers.length === 0 &&
        showEditLinks &&
        !specialInstructions) ? (
        <div className="row">
          <div className="col-xs-offset-5 col-xs-7">
            <Action
              className="cart-item_edit"
              onClick={() => {
                editMenuItem(null, orderItemId, isEditingPastOrder)
              }}
              title="Edit Menu Item"
            >
              <i className="icon-pencil" /> Edit
            </Action>
            <Action
              className="cart-item_delete"
              onClick={() => {
                deleteMenuItem(orderItemId, dropoffId)
              }}
              title="Delete Menu Item"
            >
              <i className="icon-trash" /> Remove
            </Action>
          </div>
        </div>
      ) : null}
      {mods}
    </li>
  )
}

export const CartMenuItemList = ({
  menuItems,
  previousItems,
  previousSubtotal,
  dropoffId,
  editable,
  collapsible,
  useFullSubTotal,
  deleteMenuItem,
  editMenuItem,
  showEditLinks,
  isEditingPastOrder,
  menuId,
}) => (
  <ul className="cart-item_list">
    {menuItems.length > 0 ? (
      menuItems.map((menuItem, index) => (
        <CartMenuItem
          collapsible={collapsible}
          deleteMenuItem={deleteMenuItem}
          dropoffId={dropoffId}
          editable={editable}
          editMenuItem={editMenuItem}
          isEditingPastOrder={isEditingPastOrder}
          key={index}
          menuId={menuId}
          menuItem={menuItem}
          open={menuItems.length === 1}
          showEditLinks={showEditLinks}
          useFullSubTotal={useFullSubTotal}
        />
      ))
    ) : (
      <CartNoMenuItems />
    )}
    {previousItems && previousItems.length > 0 && (
      <CartPreviousItems
        numberOfItems={previousItems.length}
        subtotal={previousSubtotal}
      />
    )}
  </ul>
)

CartMenuItemList.defaultProps = {
  menuItems: [],
}

export const AddMoreItems = ({ locationId, dropoffId }) => (
  <div className="row middle-sm">
    <div className="col-xs-12 center-xs">
      <Link to={formatUrl(placeOrderRoute.path, { locationId, dropoffId })}>
        <Action>+ Add More Items</Action>
      </Link>
    </div>
  </div>
)

export const CartCheckout = ({
  isInvalid,
  error,
  onCheckout,
  isCheckoutProcessing,
}) => (
  <div className="row middle-sm">
    <div className="col-xs-12 center-xs">
      {isCheckoutProcessing ? (
        <Submitting />
      ) : (
        <Button
          className="cart-checkout"
          disabled={isInvalid}
          mb={2}
          onClick={onCheckout}
          type="button"
        >
          Checkout
        </Button>
      )}
    </div>
    <div className="col-xs-12 center-xs">
      <InputError>{error && error.message}</InputError>
    </div>
  </div>
)

export const CartSubtotalPretax = ({
  subtotal,
  deliveryFee,
  couponApplied,
  couponAmount,
  couponCode,
  creditsApplied,
  creditsUsed,
}) => {
  const [{ subscription }] = useStateValue()

  const [isSubscribed, setIsSubscribed] = useState(null)

  useEffect(() => {
    let isMounted = true
    async function fetchData() {
      const res = await getIsSubscribed()
      if (isMounted) {
        setIsSubscribed(res)
      }
    }
    fetchData()

    return () => {
      isMounted = false
    }
  }, [])

  return (
    <div className="cart-pretax">
      {isSubscribed || subscription.value ? (
        <div className="cart-subtotal cart-delivery-fee">
          <div className="cart-subtotal_title">Unlimited Delivery:</div>
          <div
            className="cart-subtotal_price"
            style={{ textDecoration: 'line-through', fontStyle: 'italic' }}
          >
            $1.99
          </div>
        </div>
      ) : (
        <div className="cart-subtotal cart-delivery-fee">
          <div className="cart-subtotal_title">Delivery Fee:</div>
          <div className="cart-subtotal_price">
            {currency(deliveryFee, { formatWithSymbol: true }).format()}
          </div>
        </div>
      )}
      {couponApplied ? (
        <div className="cart-subtotal cart-coupons-applied">
          <div className="cart-subtotal_title">{`Coupon: ${couponCode}`}</div>
          <div className="cart-subtotal_price">
            -{currency(couponAmount, { formatWithSymbol: true }).format()}
          </div>
        </div>
      ) : null}
      {creditsApplied ? (
        <div className="cart-subtotal cart-credits-applied">
          <div className="cart-subtotal_title">Credit:</div>
          <div className="cart-subtotal_price">
            -{currency(creditsUsed, { formatWithSymbol: true }).format()}
          </div>
        </div>
      ) : null}
      <div className="cart-subtotal cart-sum">
        <div className="cart-subtotal_title">Subtotal:</div>
        <div className="cart-subtotal_price">
          {currency(subtotal, { formatWithSymbol: true }).format()}
        </div>
      </div>
    </div>
  )
}

export const CartSubtotalPosttax = ({
  taxes,
  programApplied,
  programAmount,
  programCode,
  storedCurrencyApplied,
  storedCurrencyAmount,
}) => (
  <div className="cart-posttax">
    <div className="cart-subtotal cart-taxes">
      <div className="cart-subtotal_title">Taxes:</div>
      <div className="cart-subtotal_price">
        {currency(taxes, { formatWithSymbol: true }).format()}
      </div>
    </div>
    {programApplied ? (
      <div className="cart-subtotal cart-program">
        <div className="cart-subtotal_title">{programCode}:</div>
        <div className="cart-subtotal_price">
          -{currency(programAmount, { formatWithSymbol: true }).format()}
        </div>
      </div>
    ) : null}
    {storedCurrencyApplied ? (
      <div className="cart-subtotal cart-program">
        <div className="cart-subtotal_title">Gift:</div>
        <div className="cart-subtotal_price">
          -
          {currency(storedCurrencyAmount / 100, {
            formatWithSymbol: true,
          }).format()}
        </div>
      </div>
    ) : null}
  </div>
)

export const CartSubtotalDebits = ({
  subtotal,
  deliveryFee,
  taxes,
  showTaxes,
}) => {
  return (
    <div className="cart-debits">
      <div className="cart-subtotal">
        <div className="cart-subtotal_title">Subtotal:</div>
        <div className="cart-subtotal_price">
          {currency(subtotal, { formatWithSymbol: true }).format()}
        </div>
      </div>
      <div className="cart-subtotal cart-delivery-fee">
        <div className="cart-subtotal_title">Delivery Fee:</div>
        <div className="cart-subtotal_price">
          {currency(deliveryFee, { formatWithSymbol: true }).format()}
        </div>
      </div>
      {showTaxes && (
        <div className="cart-subtotal cart-taxes">
          <div className="cart-subtotal_title">Taxes:</div>
          <div className="cart-subtotal_price">
            {currency(taxes, { formatWithSymbol: true }).format()}
          </div>
        </div>
      )}
    </div>
  )
}

CartSubtotalDebits.defaultProps = {
  showTaxes: true,
}

export const CartSubtotalCredits = ({
  couponApplied,
  couponCode,
  couponAmount,
  creditsApplied,
  creditsUsed,
}) => (
  <div className="cart-credits">
    {couponApplied ? (
      <div className="cart-subtotal cart-coupons-applied">
        <div className="cart-subtotal_title">{`Coupon: ${couponCode}`}</div>
        <div className="cart-subtotal_price">
          -{currency(couponAmount, { formatWithSymbol: true }).format()}
        </div>
      </div>
    ) : null}
    {creditsApplied ? (
      <div className="cart-subtotal cart-credits-applied">
        <div className="cart-subtotal_title">Credit:</div>
        <div className="cart-subtotal_price">
          -{currency(creditsUsed, { formatWithSymbol: true }).format()}
        </div>
      </div>
    ) : null}
  </div>
)

export const CartTotal = ({ total, totalTitle = 'Total:' }) => (
  <div className="cart-total cart-sum">
    <div className="cart-subtotal_title">{totalTitle}</div>
    <div className="cart-subtotal_price">
      {currency(total, { formatWithSymbol: true }).format()}
    </div>
  </div>
)

export const Cart = ({ children }) => (
  <div className="cart" style={{ paddingTop: '16px' }}>
    <div className="cart-title">
      <i
        className="icon icon-cart"
        style={{ color: themeConWeb.color.greyShade3 }}
      />{' '}
      Cart
    </div>
    {children}
  </div>
)

export const OrderCart = ({
  cart: { orderItems, itemSubTotal },
  dropoffId,
  error,
  isInvalid,
  deleteMenuItem,
  editMenuItem,
  onCheckout,
  isEditingPastOrder,
}) => (
  <Cart>
    <CartMenuItemList
      collapsible
      deleteMenuItem={deleteMenuItem}
      dropoffId={dropoffId}
      editable
      editMenuItem={editMenuItem}
      isEditingPastOrder={isEditingPastOrder}
      menuItems={orderItems}
      showEditLinks
      useFullSubTotal
    />
    <hr />
    <CartTotal total={itemSubTotal} totalTitle={'Subtotal:'} />
    <CartCheckout
      error={error}
      isInvalid={isInvalid || orderItems.length === 0}
      onCheckout={onCheckout}
    />
  </Cart>
)

export const CheckoutCart = ({
  cart: { orderItems, preTaxSubTotal, foodsbyFee, orderTotal, taxSubTotal },
  checkoutCartError,
  checkoutError,
  couponUsage,
  creditUsage,
  deleteMenuItem,
  dropoffId,
  editMenuItem,
  isCheckoutProcessing,
  isEditingPastOrder,
  isInvalid,
  locationId,
  onCheckout,
  programUsage = {},
  storedCurrencyUsage = {},
}) => {
  const noItems =
    checkoutCartError &&
    checkoutCartError.message &&
    checkoutCartError.message.indexOf('No items') > -1
  return (
    <Cart>
      {// When the checkout cart can't load we can't do anything
      checkoutCartError ? (
        <>
          <InputError className="cart-error">
            <i className="icon-warning" />{' '}
            {noItems
              ? 'Your cart is empty. Add more items to continue.'
              : 'There was a problem loading your cart.'}
          </InputError>
          {noItems ? (
            <AddMoreItems dropoffId={dropoffId} locationId={locationId} />
          ) : (
            <>
              <p>
                Please refresh the page and try again. If this problem persists,{' '}
                <a href={`/contact`}>contact support</a>.
              </p>
              <small>{checkoutCartError.message}</small>
            </>
          )}
        </>
      ) : (
        <>
          <CartMenuItemList
            collapsible
            deleteMenuItem={deleteMenuItem}
            dropoffId={dropoffId}
            editable
            editMenuItem={editMenuItem}
            isEditingPastOrder={isEditingPastOrder}
            menuItems={orderItems}
            showEditLinks
            useFullSubTotal
          />
          <hr />
          <div className="cart-subtotal-container">
            <CartSubtotalPretax
              couponAmount={couponUsage.couponAmount}
              couponApplied={!couponUsage.isEpp && couponUsage.couponApplied}
              couponCode={couponUsage.couponCode}
              creditsApplied={creditUsage.creditsApplied}
              creditsUsed={creditUsage.creditBalanceUsed}
              deliveryFee={foodsbyFee}
              subtotal={preTaxSubTotal}
            />
            <hr />
            {/* NOTE: the couponUsage portion of this section will need to be removed once Accounts has all EPP's established in prod */}
            <CartSubtotalPosttax
              programAmount={
                (programUsage && programUsage.programAmount) ||
                couponUsage.couponAmount
              }
              programApplied={
                (programUsage && programUsage.programApplied) ||
                (couponUsage.couponApplied && couponUsage.isEpp)
              }
              programCode={
                (programUsage && programUsage.programName) ||
                couponUsage.couponCode
              }
              storedCurrencyAmount={
                storedCurrencyUsage?.storedCurrencyBalanceUsed
              }
              storedCurrencyApplied={storedCurrencyUsage?.storedCurrencyApplied}
              taxes={taxSubTotal}
            />
          </div>
          <CartTotal total={orderTotal} totalTitle={'Total:'} />
          <CartCheckout
            error={checkoutError}
            isCheckoutProcessing={isCheckoutProcessing}
            isInvalid={isInvalid || !orderItems || orderItems.length === 0}
            onCheckout={onCheckout}
          />
          <AddMoreItems dropoffId={dropoffId} locationId={locationId} />
        </>
      )}
    </Cart>
  )
}

export default Cart
